/**
 * Build structured error details for UI display (tools page, alerts, etc.).
 */

export function parseJsonSafe(value) {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function extractEmbeddingRequestError(request) {
  if (!request || typeof request !== 'object') return null;
  const meta = parseJsonSafe(request.metadata) || request.metadata;
  const candidates = [
    meta?.error,
    meta?.error_message,
    meta?.message,
    request.error_message,
    request.error
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim()) return String(c).trim();
  }
  return null;
}

/**
 * @param {Error|Object} error
 * @param {Response|null} response
 * @returns {{ summary: string, details: string|null, debug: object|null }}
 */
export function buildErrorDisplay(error, response = null) {
  const summary =
    (error && typeof error.message === 'string' && error.message.trim()) ||
    (error && typeof error.error === 'string' && error.error.trim()) ||
    'Unknown error';

  const parts = [];
  if (response?.status) {
    parts.push(`HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`);
  }
  if (error?.code) parts.push(`code: ${error.code}`);
  if (error?.type) parts.push(`type: ${error.type}`);

  if (error?.hint) {
    parts.push(`Hint: ${error.hint}`);
  }
  if (error?.action) {
    parts.push(`What to do: ${error.action}`);
  }

  const body =
    error?.details ||
    parseJsonSafe(error?.body) ||
    parseJsonSafe(error?.responseBody) ||
    error?.debug ||
    null;

  if (body && typeof body === 'object') {
    try {
      parts.push(JSON.stringify(body, null, 2));
    } catch {
      parts.push(String(body));
    }
  } else if (typeof error?.details === 'string' && error.details.trim()) {
    parts.push(error.details.trim());
  }

  return {
    summary,
    details: parts.length ? parts.join('\n\n') : null,
    debug: body && typeof body === 'object' ? body : null
  };
}
